import {
  Link,
  PropertyFilterProps,
  TableProps,
} from "@amzn/awsui-components-console";
import { SimplifiedFixedFeeConfig } from "@amzn/fuse-accounting-central-datastore-client";
import { NumericValue } from "@smithy/core/serde";
import { TokenValue } from "aws-sdk/clients/ssm";
import { DateForm } from "src/components/allFixedFeeConfigsPage/components/date-form";
import Decimal from "decimal.js";
import React, { ReactElement } from "react";
import { LinkProps } from "@amzn/awsui-components-console/link";

const BART_DOMAIN_MAP: { [key: string]: string } = {
  "devo-FE": "beta",
  "prod-FE": "prod-jp",
  "prod-US": "prod-us",
  "prod-EU": "prod-eu",
};

export const FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS: {
  [id: string]: {
    label: string;
    minWidth: number;
    stringCell?: (item: SimplifiedFixedFeeConfig) => string | undefined;
    linkCell?: (
      item: SimplifiedFixedFeeConfig,
      domain: string,
    ) => ReactElement<LinkProps> | undefined;
    dateCell?: (item: SimplifiedFixedFeeConfig) => string | undefined;
    numberCell?: (item: SimplifiedFixedFeeConfig) => NumericValue | undefined;
    arrayCell?: (item: SimplifiedFixedFeeConfig) => string[] | undefined;
  };
} = {
  id: {
    label: "FixedFee ID",
    minWidth: 220,
    linkCell: (item: SimplifiedFixedFeeConfig, domain: string) => (
      <Link
        external
        href={`https://${BART_DOMAIN_MAP[domain]}.bart-portal.fuse.amazon.dev/getfixedfeeconfig/${item.fixedFeeId}`}
        ariaLabel={item.fixedFeeId}
      >
        {item.fixedFeeId}
      </Link>
    ),
  },
  environment: {
    label: "Environment",
    minWidth: 120,
    stringCell: (item: SimplifiedFixedFeeConfig) => item.environment,
  },
  dealStatus: {
    label: "Deal Status",
    minWidth: 120,
    stringCell: (item: SimplifiedFixedFeeConfig) => item.dealStatus,
  },
  resellerIds: {
    label: "Reseller IDs",
    minWidth: 120,
    arrayCell: (item: SimplifiedFixedFeeConfig) => item.dealConfig?.resellerIds,
  },
  fusePlanIds: {
    label: "Fuse Plan IDs",
    minWidth: 220,
    arrayCell: (item: SimplifiedFixedFeeConfig) => item.dealConfig?.fusePlanIds,
  },
  serviceProvider: {
    label: "Service Provider",
    minWidth: 120,
    stringCell: (item: SimplifiedFixedFeeConfig) => item.serviceProvider,
  },
  dealStartDate: {
    label: "Deal Start Date",
    minWidth: 120,
    dateCell: (item: SimplifiedFixedFeeConfig) => item.dealStartDate,
  },
  dealEndDate: {
    label: "Deal End Date",
    minWidth: 120,
    dateCell: (item: SimplifiedFixedFeeConfig) => item.dealEndDate,
  },
  lastBenefitEndDate: {
    label: "Last Benefit End Date",
    minWidth: 120,
    dateCell: (item: SimplifiedFixedFeeConfig) => item.lastBenefitEndDate,
  },
  contractPrice: {
    label: "Contract Price",
    minWidth: 120,
    numberCell: (item: SimplifiedFixedFeeConfig) => item.contractPrice,
  },
  initialPlanPrice: {
    label: "Initial Plan Price",
    minWidth: 120,
    numberCell: (item: SimplifiedFixedFeeConfig) => item.initialPlanPrice,
  },
  currencyCode: {
    label: "Currency Code",
    minWidth: 120,
    stringCell: (item: SimplifiedFixedFeeConfig) => item.contractCurrencyCode,
  },
  marketplaceId: {
    label: "Marketplace ID",
    minWidth: 120,
    stringCell: (item: SimplifiedFixedFeeConfig) =>
      item.dealConfig?.marketplaceId,
  },
  accountingRegistrationId: {
    label: "Accounting Registration ID",
    minWidth: 220,
    stringCell: (item: SimplifiedFixedFeeConfig) =>
      item.accountingRegistrationId,
  },
};

export const FIXED_FEE_CONFIG_ATTRIBUTES: string[] = Object.keys(
  FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS,
);
export const FIXED_FEE_CONFIG_FILTERS: PropertyFilterProps.FilteringProperty[] =
  FIXED_FEE_CONFIG_ATTRIBUTES.map((attr) => {
    const { label, stringCell, linkCell, dateCell, numberCell, arrayCell } =
      FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[attr];

    let defaultOperator = undefined;
    let operators: (
      | PropertyFilterProps.ComparisonOperator
      | PropertyFilterProps.ExtendedOperator<TokenValue>
    )[] = [];

    if (stringCell || linkCell) {
      defaultOperator = "=";
      operators = ["=", "!="];
    } else if (dateCell) {
      defaultOperator = ">=";
      operators = ["<", "<=", ">", ">="].map((operator) => ({
        operator,
        form: DateForm,
        format: (date: string) => date,
        match: "datetime" as const,
      }));
    } else if (numberCell) {
      defaultOperator = "=";
      operators = ["=", "!=", "<", "<=", ">", ">="];
    } else if (arrayCell) {
      defaultOperator = ":";
      operators = [":", "!:"];
    }

    return {
      key: attr,
      defaultOperator,
      operators: operators,
      propertyLabel: label,
      groupValuesLabel: label + " values",
    };
  });

export const STRING_OR_DATE_CELL_WRAPPER: { (item?: string): string } = (
  item,
) => item ?? "";
export const LINK_CELL_WRAPPER: { (item?: ReactElement<LinkProps>): string } = (
  item,
) => item?.props?.ariaLabel ?? "";
export const NUMBER_CELL_WRAPPER: {
  (item?: NumericValue | Decimal): string;
} = (item) =>
  item
    ? Number(
        item instanceof NumericValue ? item.string : item.toString(),
      ).toLocaleString("en-US", {
        maximumFractionDigits: 4,
      })
    : "";
export const ARRAY_CELL_WRAPPER: { (item?: string[]): string } = (item) =>
  item?.join(", ") ?? "";

export const getFixedFeeConfigTableColDefs = (
  domain: string,
): TableProps.ColumnDefinition<SimplifiedFixedFeeConfig>[] =>
  FIXED_FEE_CONFIG_ATTRIBUTES.map((attr) => {
    const {
      label,
      minWidth,
      stringCell,
      dateCell,
      linkCell,
      numberCell,
      arrayCell,
    } = FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[attr];

    let cellWrapper: { (item: SimplifiedFixedFeeConfig): ReactElement };
    if (stringCell) {
      cellWrapper = (item: SimplifiedFixedFeeConfig) => (
        <span>{STRING_OR_DATE_CELL_WRAPPER(stringCell(item))}</span>
      );
    } else if (dateCell) {
      cellWrapper = (item: SimplifiedFixedFeeConfig) => (
        <span>{STRING_OR_DATE_CELL_WRAPPER(dateCell(item))}</span>
      );
    } else if (linkCell) {
      cellWrapper = (item: SimplifiedFixedFeeConfig) =>
        linkCell(item, domain) ?? <></>;
    } else if (numberCell) {
      cellWrapper = (item: SimplifiedFixedFeeConfig) => (
        <span>{NUMBER_CELL_WRAPPER(numberCell(item))}</span>
      );
    } else if (arrayCell) {
      cellWrapper = (item: SimplifiedFixedFeeConfig) => (
        <span>{ARRAY_CELL_WRAPPER(arrayCell(item))}</span>
      );
    }

    return {
      id: attr,
      sortingField: attr,
      header: label,
      cell: cellWrapper!,
      minWidth,
    };
  });
